import { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/getCurrentFleet";

/**
 * Per-user notification / communication preferences.
 *
 * NOTE (architecture): the backend does not yet expose a per-user notification
 * preference endpoint (`/parametre` only stores global editable/supprimable
 * flags). To avoid shipping a UI wired to a 404, preferences are persisted
 * locally, keyed by user id. Everything funnels through `persist()` below —
 * that single function is the swap point: replace its body with a React-Query
 * mutation (e.g. PUT /user/notification-preferences) once the endpoint exists,
 * and the Settings UI keeps working unchanged.
 */

export type NotificationPreferenceId =
  | "contract_ending"
  | "maintenance_due"
  | "claim_activity"
  | "document_expiry";

export type NotificationPreference = {
  id: NotificationPreferenceId;
  /** i18n key + human fallback, resolved by the consumer via t(). */
  labelKey: string;
  defaultLabel: string;
  descriptionKey: string;
  defaultDescription: string;
  enabled: boolean;
};

const DEFAULT_PREFERENCES: NotificationPreference[] = [
  {
    id: "contract_ending",
    labelKey: "setting.notif.contractEnding",
    defaultLabel: "Vehicle contract endings",
    descriptionKey: "setting.notif.contractEndingDesc",
    defaultDescription: "Be alerted when a vehicle contract is about to expire.",
    enabled: true,
  },
  {
    id: "maintenance_due",
    labelKey: "setting.notif.maintenanceDue",
    defaultLabel: "Maintenance & technical inspection",
    descriptionKey: "setting.notif.maintenanceDueDesc",
    defaultDescription:
      "Reminders for servicing and technical inspection due dates.",
    enabled: false,
  },
  {
    id: "claim_activity",
    labelKey: "setting.notif.claimActivity",
    defaultLabel: "Claims activity",
    descriptionKey: "setting.notif.claimActivityDesc",
    defaultDescription: "New claims and status updates.",
    enabled: true,
  },
  {
    id: "document_expiry",
    labelKey: "setting.notif.documentExpiry",
    defaultLabel: "Document expiry",
    descriptionKey: "setting.notif.documentExpiryDesc",
    defaultDescription:
      "Registration cards, insurance and licenses nearing expiry.",
    enabled: false,
  },
];

const storageKey = (userId: number | string) =>
  `notificationPreferences:${userId}`;

const loadPreferences = (userId: number | string): NotificationPreference[] => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_PREFERENCES;
    const saved = JSON.parse(raw) as Partial<
      Record<NotificationPreferenceId, boolean>
    >;
    // Merge saved flags onto defaults so new categories appear automatically.
    return DEFAULT_PREFERENCES.map((pref) => ({
      ...pref,
      enabled:
        typeof saved[pref.id] === "boolean"
          ? (saved[pref.id] as boolean)
          : pref.enabled,
    }));
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const useNotificationPreferences = () => {
  const userId = getCurrentUser()?.id ?? "anonymous";

  const initial = useMemo(() => loadPreferences(userId), [userId]);
  const [preferences, setPreferences] =
    useState<NotificationPreference[]>(initial);
  const [saved, setSaved] = useState<NotificationPreference[]>(initial);
  const [isSaving, setIsSaving] = useState(false);

  // Re-hydrate if the identity changes (e.g. account switch).
  useEffect(() => {
    setPreferences(initial);
    setSaved(initial);
  }, [initial]);

  const toggle = useCallback((id: NotificationPreferenceId) => {
    setPreferences((prev) =>
      prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p))
    );
  }, []);

  const isDirty = useMemo(
    () => preferences.some((p, i) => p.enabled !== saved[i]?.enabled),
    [preferences, saved]
  );

  /** SWAP POINT — replace the localStorage write with a real API mutation. */
  const persist = useCallback(
    async (next: NotificationPreference[]) => {
      const payload = next.reduce(
        (acc, p) => ({ ...acc, [p.id]: p.enabled }),
        {} as Record<NotificationPreferenceId, boolean>
      );
      localStorage.setItem(storageKey(userId), JSON.stringify(payload));
    },
    [userId]
  );

  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      await persist(preferences);
      setSaved(preferences);
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [persist, preferences]);

  const reset = useCallback(() => setPreferences(saved), [saved]);

  return { preferences, toggle, save, reset, isDirty, isSaving };
};
